import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setTag('feature_area', 'admin_panel');
      scope.setTag('error_boundary', 'admin');
      scope.setExtra('componentStack', errorInfo.componentStack);
      Sentry.captureException(error);
    });
    console.error('[Admin] Error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--background, #0f0f0f)',
          fontFamily: 'var(--font-sans, Inter, system-ui, sans-serif)',
          padding: 24,
        }}>
          <div style={{
            background: 'var(--surface, #1a1a1a)',
            borderRadius: 16,
            padding: 48,
            maxWidth: 520,
            width: '100%',
            textAlign: 'center',
            border: '1px solid var(--border, rgba(255,255,255,0.08))',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>⚠️</div>
            <h1 style={{
              color: 'var(--text, #fff)',
              fontSize: 22,
              fontWeight: 700,
              margin: '0 0 8px',
            }}>
              Admin Panel — Fehler aufgetreten
            </h1>
            <p style={{
              color: 'var(--text-muted, rgba(255,255,255,0.5))',
              fontSize: 14,
              lineHeight: 1.6,
              margin: '0 0 28px',
            }}>
              Ein unerwarteter Fehler ist aufgetreten. Versuche die Seite neu zu laden.
            </p>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                style={{
                  background: '#3B82F6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                🔄 Neu laden
              </button>
              <button
                onClick={this.handleReset}
                style={{
                  background: 'transparent',
                  color: 'var(--text-muted, rgba(255,255,255,0.5))',
                  border: '1px solid var(--border, rgba(255,255,255,0.1))',
                  borderRadius: 10,
                  padding: '12px 24px',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Cache leeren
              </button>
            </div>

            {this.state.error && (
              <details style={{
                marginTop: 24,
                textAlign: 'left',
                background: 'rgba(239,68,68,0.08)',
                borderRadius: 8,
                padding: 12,
              }}>
                <summary style={{ color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 11 }}>
                  Technische Details
                </summary>
                <pre style={{
                  color: '#EF4444',
                  fontSize: 11,
                  overflow: 'auto',
                  marginTop: 8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}>
                  {this.state.error.message}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AdminErrorBoundary;
